// SMS send-delay editor — embedded in the Settings page.
//
// The global SMS send-delay (config_key "sms_send_delay_ms") is the number of
// minutes to wait after a lead pulls before they become eligible for an SMS
// text, giving them a chance to call in first. It is stored in milliseconds in
// the existing config table (no dedicated table) and read fresh by the SMS
// dispatch Lambda each tick. This editor lets an operator view and adjust the
// delay in minutes; the value persists via the admin API as milliseconds.

import { useCallback, useEffect, useState } from "react";

import { api } from "../lib/api";
import { buttonStyle, Card, Pill, Skeleton, tokens } from "../lib/theme";

const MAX_DELAY_MS = 86_400_000; // 24 hours

export default function SmsSendDelayEditor() {
  const [delayMs, setDelayMs] = useState<number | null>(null);
  const [source, setSource] = useState<"stored" | "default" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .getSmsDelay()
      .then((d) => {
        setDelayMs(d.sendDelayMs);
        setSource(d.source);
        setError(null);
        setDirty(false);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onMinutesChange = (raw: string) => {
    const minutes = Number(raw);
    if (raw === "" || Number.isNaN(minutes)) {
      setDelayMs(null);
      setDirty(true);
      return;
    }
    setDelayMs(Math.round(minutes * 60 * 1000));
    setDirty(true);
  };

  const save = useCallback(() => {
    if (delayMs == null) return;
    if (!(delayMs > 0 && delayMs <= MAX_DELAY_MS)) {
      return setError("Delay must be between 1 minute and 24 hours.");
    }
    setSaving(true);
    api
      .putSmsDelay(delayMs)
      .then((d) => {
        setDelayMs(d.sendDelayMs);
        setSource(d.source);
        setError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Save failed"))
      .finally(() => setSaving(false));
  }, [delayMs]);

  const inputStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.xs}px ${tokens.space.sm}px`,
    fontSize: tokens.font.size.sm,
    width: 120,
  };

  const minutesValue = delayMs == null ? "" : delayMs / 1000 / 60;

  return (
    <Card
      title="SMS send delay"
      style={{ marginBottom: tokens.space.xl }}
      action={
        <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
          {source && <Pill tone={source === "stored" ? "success" : "neutral"}>{source}</Pill>}
          {savedAt && (
            <span style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}>
              Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            style={buttonStyle("primary", saving || !dirty)}
          >
            {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
      }
    >
      <p style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm, marginTop: 0 }}>
        Minutes to wait after a lead pulls before texting them, so they have a
        chance to call in first. Applies globally across all SMS campaigns.
      </p>

      {error && (
        <div style={{ color: tokens.color.danger, marginBottom: tokens.space.sm }}>{error}</div>
      )}

      {loading && delayMs == null ? (
        <>
          <Skeleton height={20} width={140} />
          <div style={{ height: 8 }} />
          <Skeleton height={36} width={140} />
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
          <label
            htmlFor="sms-send-delay-minutes"
            style={{
              fontSize: tokens.font.size.xs,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: tokens.color.fg1,
              fontWeight: 600,
            }}
          >
            Delay (minutes)
          </label>
          <input
            id="sms-send-delay-minutes"
            type="number"
            min="1"
            max="1440"
            value={minutesValue}
            onChange={(e) => onMinutesChange(e.target.value)}
            style={inputStyle}
            aria-label="Delay (minutes)"
          />
        </div>
      )}
    </Card>
  );
}
